import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Tooltip,
  Chip,
  useTheme,
  Switch,
  FormControlLabel,
  Button,
  ButtonGroup,
  Paper,
  Divider,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  RestartAlt as ResetIcon
} from '@mui/icons-material';
import { fetchDifficultyDistribution } from '../../utils/api';

const DIFFICULTY_COLORS = {
  1: '#4CAF50', // Green for easy
  2: '#8BC34A', // Light green for easy-medium
  3: '#FFC107', // Yellow for medium
  4: '#FF9800', // Orange for hard
  5: '#F44336', // Red for very hard
  'no_rating': '#9E9E9E' // Gray for no rating
};

const DIFFICULTY_LABELS = {
  1: 'Very Easy (1)',
  2: 'Easy (2)',
  3: 'Medium (3)',
  4: 'Hard (4)',
  5: 'Very Hard (5)',
  'no_rating': 'No Rating'
};

const RANGE_COLORS = {
  easy: '#4CAF50',
  medium: '#FFC107',
  hard: '#F44336'
};

// Preset configurations
const PRESETS = {
  current: {
    name: 'Current Backend',
    description: 'Easy: 1-2, Medium: 3, Hard: 3-5',
    ranges: {
      easy: [1, 2],
      medium: [3],
      hard: [3, 4, 5]
    }
  },
  proposed: {
    name: 'Proposed Overlapping',
    description: 'Easy: 1-2, Medium: 2-3, Hard: 3-5',
    ranges: {
      easy: [1, 2],
      medium: [2, 3],
      hard: [3, 4, 5]
    }
  },
  noOverlap: {
    name: 'No Overlap',
    description: 'Easy: 1-2, Medium: 3, Hard: 4-5',
    ranges: {
      easy: [1, 2],
      medium: [3],
      hard: [4, 5]
    }
  },
  balanced: {
    name: 'Balanced Distribution',
    description: 'Easy: 1, Medium: 2-4, Hard: 5',
    ranges: {
      easy: [1],
      medium: [2, 3, 4],
      hard: [5]
    }
  }
};

const RangeEditor = ({ ranges, onRangesChange, onSave, onReset }) => {
  const [localRanges, setLocalRanges] = useState(ranges);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const handleDifficultyToggle = (category, difficulty) => {
    const newRanges = { ...localRanges };
    const currentRange = newRanges[category];
    
    if (currentRange.includes(difficulty)) {
      newRanges[category] = currentRange.filter(d => d !== difficulty);
    } else {
      newRanges[category] = [...currentRange, difficulty].sort();
    }
    
    setLocalRanges(newRanges);
  };

  const handleApply = () => {
    onRangesChange(localRanges);
    setEditDialogOpen(false);
  };

  const handlePresetSelect = (presetKey) => {
    const preset = PRESETS[presetKey];
    setLocalRanges(preset.ranges);
    onRangesChange(preset.ranges);
  };

  const handleSave = () => {
    onSave(localRanges);
  };

  const handleReset = () => {
    const currentRanges = PRESETS.current.ranges;
    setLocalRanges(currentRanges);
    onRangesChange(currentRanges);
    onReset();
  };

  const formatRangeDisplay = (range) => {
    if (range.length === 0) return 'None';
    if (range.length === 1) return range[0].toString();
    const sorted = [...range].sort();
    return `${sorted[0]}-${sorted[sorted.length - 1]}`;
  };

  const getDifficultyOverlaps = () => {
    const overlaps = {};
    for (let i = 1; i <= 5; i++) {
      const categories = [];
      Object.entries(localRanges).forEach(([category, range]) => {
        if (range.includes(i)) categories.push(category);
      });
      if (categories.length > 1) {
        overlaps[i] = categories;
      }
    }
    return overlaps;
  };

  const overlaps = getDifficultyOverlaps();

  return (
    <Box>
      {/* Range Display and Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Custom Ranges
        </Typography>
        <ButtonGroup size="small">
          <Button
            startIcon={<EditIcon />}
            onClick={() => setEditDialogOpen(true)}
            variant="outlined"
          >
            Edit
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSave}
            variant="outlined"
            color="primary"
          >
            Save
          </Button>
          <Button
            startIcon={<ResetIcon />}
            onClick={handleReset}
            variant="outlined"
            color="secondary"
          >
            Reset
          </Button>
        </ButtonGroup>
      </Box>

      {/* Current Range Display */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
        <Grid container spacing={2}>
          {Object.entries(ranges).map(([category, range]) => (
            <Grid item xs={4} key={category}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </Typography>
                <Chip
                  label={formatRangeDisplay(range)}
                  sx={{
                    bgcolor: RANGE_COLORS[category],
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
        
        {/* Overlap Warning */}
        {Object.keys(overlaps).length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Overlaps detected:</strong> {' '}
              {Object.entries(overlaps).map(([difficulty, categories]) => 
                `Difficulty ${difficulty} appears in ${categories.join(', ')}`
              ).join('; ')}
            </Typography>
          </Alert>
        )}
      </Paper>

      {/* Preset Buttons */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Quick Presets:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {Object.entries(PRESETS).map(([key, preset]) => (
            <Button
              key={key}
              size="small"
              variant="outlined"
              onClick={() => handlePresetSelect(key)}
              sx={{ textTransform: 'none' }}
            >
              {preset.name}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Difficulty Ranges</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Select which difficulty levels belong to each category. Multiple categories can include the same difficulty level.
          </Typography>
          
          <Grid container spacing={3}>
            {Object.entries(localRanges).map(([category, range]) => (
              <Grid item xs={12} md={4} key={category}>
                <Typography variant="h6" sx={{ mb: 2, textTransform: 'capitalize' }}>
                  {category}
                </Typography>
                <FormGroup>
                  {[1, 2, 3, 4, 5].map(difficulty => (
                    <FormControlLabel
                      key={difficulty}
                      control={
                        <Checkbox
                          checked={range.includes(difficulty)}
                          onChange={() => handleDifficultyToggle(category, difficulty)}
                          sx={{
                            color: DIFFICULTY_COLORS[difficulty],
                            '&.Mui-checked': {
                              color: DIFFICULTY_COLORS[difficulty],
                            },
                          }}
                        />
                      }
                      label={`Difficulty ${difficulty}`}
                    />
                  ))}
                </FormGroup>
              </Grid>
            ))}
          </Grid>

          {/* Live Preview of Overlaps */}
          {Object.keys(getDifficultyOverlaps()).length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Preview overlaps:</strong> {' '}
                {Object.entries(getDifficultyOverlaps()).map(([difficulty, categories]) => 
                  `Difficulty ${difficulty} → ${categories.join(', ')}`
                ).join('; ')}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleApply} variant="contained">Apply Changes</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default function DifficultyDistribution() {
  const theme = useTheme();
  const [distribution, setDistribution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customRanges, setCustomRanges] = useState(PRESETS.proposed.ranges);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    loadDistribution();
  }, []);

  const loadDistribution = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchDifficultyDistribution();
      setDistribution(response.data);
    } catch (err) {
      console.error('Error loading difficulty distribution:', err);
      setError('Failed to load difficulty distribution');
    } finally {
      setLoading(false);
    }
  };

  const calculateRangeDistribution = (ranges) => {
    if (!distribution) return {};
    
    const result = {};
    Object.entries(ranges).forEach(([label, difficultyValues]) => {
      result[label] = difficultyValues.reduce((sum, difficulty) => {
        return sum + (distribution[`difficulty_${difficulty}`] || 0);
      }, 0);
    });
    return result;
  };

  const formatChartData = () => {
    if (!distribution) return [];
    
    return [
      { name: 'Diff 1', value: distribution.difficulty_1, fill: DIFFICULTY_COLORS[1] },
      { name: 'Diff 2', value: distribution.difficulty_2, fill: DIFFICULTY_COLORS[2] },
      { name: 'Diff 3', value: distribution.difficulty_3, fill: DIFFICULTY_COLORS[3] },
      { name: 'Diff 4', value: distribution.difficulty_4, fill: DIFFICULTY_COLORS[4] },
      { name: 'Diff 5', value: distribution.difficulty_5, fill: DIFFICULTY_COLORS[5] },
      { name: 'No Rating', value: distribution.no_rating, fill: DIFFICULTY_COLORS.no_rating }
    ].filter(item => item.value > 0);
  };

  const formatRangeData = (ranges) => {
    const rangeDistribution = calculateRangeDistribution(ranges);
    return Object.entries(rangeDistribution).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value,
      fill: RANGE_COLORS[name]
    }));
  };

  const handleRangesChange = (newRanges) => {
    setCustomRanges(newRanges);
  };

  const handleSaveConfiguration = (ranges) => {
    // For now, just show a success message
    // Later this could save to backend or localStorage
    setSnackbar({
      open: true,
      message: 'Configuration saved locally. This could be applied to backend in the future.',
      severity: 'success'
    });
  };

  const handleResetToDefault = () => {
    setCustomRanges(PRESETS.current.ranges);
    setSnackbar({
      open: true,
      message: 'Reset to current backend configuration',
      severity: 'info'
    });
  };

  const exportConfiguration = () => {
    const config = {
      timestamp: new Date().toISOString(),
      ranges: customRanges,
      distribution: distribution
    };
    
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `difficulty-ranges-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    setSnackbar({
      open: true,
      message: 'Configuration exported to file',
      severity: 'success'
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  const currentRangeData = formatRangeData(PRESETS.current.ranges);
  const customRangeData = formatRangeData(customRanges);

  return (
    <Box>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Question Difficulty Distribution
            </Typography>
            <Button
              startIcon={<CopyIcon />}
              onClick={exportConfiguration}
              variant="outlined"
              size="small"
            >
              Export Config
            </Button>
          </Box>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            Distribution of difficulty ratings across {distribution?.total || 0} valid question units
          </Typography>

          <Grid container spacing={3}>
            {/* Individual Difficulty Levels */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                By Individual Rating
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={formatChartData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="value" />
                </BarChart>
              </ResponsiveContainer>
            </Grid>

            {/* Summary Statistics */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Summary Statistics
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  {[1, 2, 3, 4, 5, 'no_rating'].map((difficulty) => (
                    <Grid item xs={6} key={difficulty}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            backgroundColor: DIFFICULTY_COLORS[difficulty],
                            borderRadius: 1
                          }}
                        />
                        <Typography variant="body2">
                          {DIFFICULTY_LABELS[difficulty]}:
                        </Typography>
                        <Chip
                          label={distribution[difficulty === 'no_rating' ? 'no_rating' : `difficulty_${difficulty}`] || 0}
                          size="small"
                          sx={{ ml: 'auto' }}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>

            {/* Current Range Grouping */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Current Backend Ranges
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Easy: 1-2, Medium: 3, Hard: 3-5
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={currentRangeData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(1)}%)`}
                  >
                    {currentRangeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Grid>

            {/* Custom Range Grouping */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Custom Ranges (Editable)
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={customRangeData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(1)}%)`}
                  >
                    {customRangeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Grid>

            {/* Range Editor */}
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Range Configuration Editor</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <RangeEditor
                    ranges={customRanges}
                    onRangesChange={handleRangesChange}
                    onSave={handleSaveConfiguration}
                    onReset={handleResetToDefault}
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Impact Analysis */}
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Impact Analysis
                </Typography>
                <Typography variant="body2">
                  Use the range editor above to experiment with different difficulty groupings. 
                  The charts will update in real-time to show how your changes affect question distribution. 
                  Export your configuration when ready to implement in the backend.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}